import type { z } from "zod";

import { useUser } from "@clerk/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { clubCountryCodes, clubInformationsSchema } from "@teamz/common";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import PhoneInput from "react-phone-number-input/input";
import { useNavigate } from "react-router";

import { useTranslationUtils } from "@/hooks/use-translation-utils";
import { tDyn } from "@/lib/i18n-dynamic";
import { useTRPC } from "@/lib/trpc";
import { routes } from "@/routes";

import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { ErrorField, RootField, TextField } from "../ui/text-field";

const firstStepSchema = clubInformationsSchema.pick(
  {
    clubRepresentativeFirstName: true,
    clubRepresentativeLastName: true,
    clubRepresentativeEmail: true,
    clubRepresentativePhone: true,
  },
);
type FirstStepSchema = z.infer<typeof firstStepSchema>;

const secondStepSchema = clubInformationsSchema.pick(
  {
    clubName: true,
    clubAddress: true,
    clubCity: true,
    clubCountryCode: true,
    clubEmail: true,
    clubPhone: true,
  },
);
type SecondStepSchema = z.infer<typeof secondStepSchema>;

function OnboardingFirstStepForm({ data, setData }: { data?: FirstStepSchema; setData: (data: FirstStepSchema) => void }) {
  const { t } = useTranslation(["common"]);
  const firstStepForm = useForm<FirstStepSchema>({
    resolver: zodResolver(firstStepSchema),
    defaultValues: data,
  });

  const onSubmit = firstStepForm.handleSubmit((data) => {
    setData(data);
  });

  const fistFormError = firstStepForm.formState.errors;

  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-4 px-4">
      <TextField
        label={t("common:firstname")}
        errorMessage={tDyn(t, fistFormError.clubRepresentativeFirstName?.message)}
        {...firstStepForm.register("clubRepresentativeFirstName")}
      />
      <TextField
        label={t("common:lastname")}
        errorMessage={tDyn(t, fistFormError.clubRepresentativeLastName?.message)}
        {...firstStepForm.register("clubRepresentativeLastName")}
      />
      <TextField
        label="Email"
        errorMessage={tDyn(t, fistFormError.clubRepresentativeEmail?.message)}
        {...firstStepForm.register("clubRepresentativeEmail")}
      />
      <Controller
        control={firstStepForm.control}
        name="clubRepresentativePhone"
        render={({ field, fieldState }) => (
          <RootField>
            <Label>{t("common:phone")}</Label>
            <PhoneInput
              inputComponent={Input}
              placeholder="+33 6 12 34 56 78"
              onChange={(value) => {
                if (!value || value === "+") {
                  field.onChange("");
                  return;
                }
                field.onChange(value);
              }}
              value={field.value || ""}
            />
            {fieldState.error?.message && (
              <ErrorField>{tDyn(t, fieldState.error?.message)}</ErrorField>
            )}
          </RootField>
        )}
      />

      <div className="flex w-full items-center justify-center gap-3 px-4 pb-4">
        <Button
          type="submit"
          className="w-44"
          loading={firstStepForm.formState.isSubmitting}
        >
          {t("common:next")}
        </Button>
      </div>
    </form>
  );
}

function OnboardingSecondStepForm({ firstStepData, getBack }: { firstStepData: FirstStepSchema; getBack: () => void }) {
  const { t } = useTranslation(["common"]);
  const navigate = useNavigate();
  const { user } = useUser();
  const { getCountryName } = useTranslationUtils();
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  const { mutate, isPending } = useMutation(trpc.club.createClub.mutationOptions({
    onSuccess: async () => {
      await user?.reload();
      // `getMe` est déjà en cache avec hasProfile: false (garde d'onboarding) :
      // sans invalidation, `Layout` renverrait aussitôt sur ce formulaire.
      await queryClient.invalidateQueries(trpc.user.getMe.queryFilter());
      // Un club fraîchement inscrit arrive sur la liste joueurs (teaser anonymisé
      // s'il n'est pas abonné) : c'est l'écran qui vend le produit, là où le profil
      // vide ne montre rien.
      navigate(routes.players);
    },
  }));

  const secondStepForm = useForm<SecondStepSchema>({
    resolver: zodResolver(secondStepSchema),
  });

  const onSubmit = secondStepForm.handleSubmit((data) => {
    mutate({ ...firstStepData, ...data });
  });

  const secondStepFormError = secondStepForm.formState.errors;

  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-4 px-4">
      <TextField
        label={t("common:club-name")}
        errorMessage={tDyn(t, secondStepFormError.clubName?.message)}
        {...secondStepForm.register("clubName")}
      />
      <TextField
        label={t("common:address")}
        errorMessage={tDyn(t, secondStepFormError.clubAddress?.message)}
        {...secondStepForm.register("clubAddress")}
      />
      <TextField
        label={t("common:city")}
        errorMessage={tDyn(t, secondStepFormError.clubCity?.message)}
        {...secondStepForm.register("clubCity")}
      />

      <Controller
        control={secondStepForm.control}
        name="clubCountryCode"
        render={({ field, fieldState }) => (
          <RootField>
            <Label>{t("common:country")}</Label>
            <Select
              value={field.value}
              onValueChange={field.onChange}
            >
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>

              <SelectContent>
                <SelectGroup>
                  {clubCountryCodes.map(country => (
                    <SelectItem key={country} value={country}>
                      {getCountryName(country)}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
            {fieldState.error?.message && (
              <ErrorField>{tDyn(t, fieldState.error?.message)}</ErrorField>
            )}
          </RootField>
        )}
      />

      <TextField
        label="Email"
        errorMessage={tDyn(t, secondStepFormError.clubEmail?.message)}
        {...secondStepForm.register("clubEmail")}
      />

      <Controller
        control={secondStepForm.control}
        name="clubPhone"
        render={({ field, fieldState }) => (
          <RootField>
            <Label>{t("common:phone")}</Label>
            <PhoneInput
              inputComponent={Input}
              placeholder="+33 2 12 34 56 78"
              onChange={(value) => {
                if (!value || value === "+") {
                  field.onChange("");
                  return;
                }
                field.onChange(value);
              }}
              value={field.value || ""}
            />
            {fieldState.error?.message && (
              <ErrorField>{tDyn(t, fieldState.error?.message)}</ErrorField>
            )}
          </RootField>
        )}
      />

      <div className="flex w-full items-center justify-center gap-3 px-4 pb-4">
        <Button
          type="button"
          variant="dark"
          onClick={getBack}
          className="w-full"
        >
          {t("common:back")}
        </Button>
        <Button
          type="submit"
          loading={isPending || secondStepForm.formState.isSubmitting}
          className="w-full"
        >
          {t("common:save")}
        </Button>
      </div>
    </form>
  );
}

export function ClubOnboardingForm() {
  const [step, setStep] = useState<1 | 2>(1);
  const [firstStepDataInfos, setFirstStepDataInfos] = useState<FirstStepSchema>();
  const { t } = useTranslation(["profile"]);

  return (
    <div
      className="flex w-96 flex-col gap-6 rounded-2xl bg-neutral-700"
    >
      <div className="border-b border-neutral-500 p-4 text-center">
        <span className="text-base font-semibold">
          {
            step === 1
              ? `${t("profile:club-representative")} 1/2`
              : `${t("profile:club-details")} 2/2`
          }
        </span>
      </div>
      {step === 1 && (
        <OnboardingFirstStepForm
          data={firstStepDataInfos}
          setData={(data) => {
            setFirstStepDataInfos(data);
            setStep(2);
          }}
        />
      )}
      {((step === 2 && firstStepDataInfos)
        && <OnboardingSecondStepForm firstStepData={firstStepDataInfos} getBack={() => setStep(1)} />
      )}
    </div>
  );
}
